import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getAvatarUrl, type AvatarKey } from '@/constants/avatars';
import {
  useChildList,
  useSetDefaultChild,
  GRADE_LABELS,
  type ChildGrade,
  type ChildProfileListItem,
} from '@/hooks/useChild';
import { Check, Loader2, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { analytics } from '@/lib/analytics';

interface ChildSwitchDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Current child ID */
  currentChildId?: string;
  /** Callback when child is switched successfully */
  onSwitch?: (childId: string) => void;
}

/**
 * ChildSwitchDialog - Child switching dialog
 *
 * Dialog style design (corresponds to App's ActionSheet):
 * - Show all children list
 * - Current child has checkmark
 * - Click to switch default child
 */
export function ChildSwitchDialog({
  open,
  onOpenChange,
  currentChildId,
  onSwitch,
}: ChildSwitchDialogProps) {
  // Fetch all children
  const { data: childrenData, isLoading } = useChildList({ enabled: open });
  const children = childrenData?.data || [];

  // Set default child mutation
  const { mutate: setDefault, isPending } = useSetDefaultChild();

  const handleSelectChild = (child: ChildProfileListItem) => {
    // Determine if already selected - use currentChildId as source of truth when available,
    // only fall back to isDefault when currentChildId is undefined (to avoid race conditions)
    const isAlreadySelected = currentChildId ? child.id === currentChildId : child.isDefault;

    if (isAlreadySelected) {
      onOpenChange(false);
      return;
    }

    setDefault(child.id, {
      onSuccess: () => {
        toast.success(`Switched to ${child.nickname}`);
        // Track child profile selected event
        analytics.trackChildProfileSelected({
          child_id: child.id,
          nickname: child.nickname,
          grade: child.grade,
        });
        onSwitch?.(child.id);
        onOpenChange(false);
      },
      onError: () => {
        toast.error('Failed to switch, please try again');
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">Switch Child</DialogTitle>
        </DialogHeader>

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        )}

        {/* Children List */}
        {!isLoading && (
          <div className="space-y-2 py-2">
            {children.map((child) => {
              // Use currentChildId as source of truth when available,
              // only fall back to isDefault when currentChildId is undefined
              const isSelected = currentChildId ? child.id === currentChildId : child.isDefault;

              return (
                <button
                  key={child.id}
                  onClick={() => handleSelectChild(child)}
                  disabled={isPending}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-xl px-4 py-3 transition-colors',
                    isSelected ? 'bg-violet-50' : 'hover:bg-gray-50 active:bg-gray-100',
                    isPending && 'cursor-not-allowed opacity-50'
                  )}
                >
                  {/* Avatar */}
                  <Avatar className={cn('h-12 w-12', isSelected ? 'bg-violet-100' : 'bg-gray-100')}>
                    <AvatarImage
                      src={getAvatarUrl(child.avatar as AvatarKey)}
                      alt={child.nickname}
                    />
                    <AvatarFallback>{child.nickname.charAt(0)}</AvatarFallback>
                  </Avatar>

                  {/* Info */}
                  <div className="min-w-0 flex-1 text-left">
                    <p
                      className={cn(
                        'truncate text-base font-semibold',
                        isSelected ? 'text-violet-700' : 'text-gray-900'
                      )}
                    >
                      {child.nickname}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        {GRADE_LABELS[child.grade as ChildGrade] || child.grade}
                      </span>
                      <span className="text-xs text-gray-400">·</span>
                      <span className="text-xs text-gray-400">{child.taskCount} tasks</span>
                    </div>
                  </div>

                  {/* Check mark */}
                  {isSelected && <Check className="h-6 w-6 shrink-0 text-violet-500" />}
                </button>
              );
            })}

            {/* Empty state */}
            {children.length === 0 && (
              <div className="flex flex-col items-center py-8">
                <p className="text-sm text-gray-400">No child profiles yet</p>
              </div>
            )}

            {/* Add child hint (if only one child) */}
            {children.length === 1 && (
              <div className="mx-2 mt-4 flex items-center gap-2 rounded-lg bg-gray-50 p-3">
                <Info className="h-4 w-4 shrink-0 text-gray-400" />
                <p className="text-xs text-gray-500">You can add more child profiles in your profile</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
